// src/components/AboutMeModal.js
import React from 'react';
import FormattedText from './FormattedText';

export default function AboutMeModal({ siteContent, onClose, onNavigate }) {
    if (!siteContent) return null;
    const c = siteContent;
    const hasPhoto = c.aboutPhoto && c.aboutPhoto.trim();
    const headline = c.aboutHeadline || 'Hey, I\u2019m Danielle!';
    const tagline = c.aboutTagline || '';
    const intro = c.aboutIntro || '';
    const body = c.aboutBody || '';
    const closingHeadline = c.aboutClosingHeadline || '';
    const closing = c.aboutClosing || '';
    const ctaText = c.aboutCtaText || 'Browse Programs';
    const ctaLink = c.aboutCtaLink || '/programs';

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
            {/* Backdrop — blur + dark overlay */}
            <div style={{
                position: 'absolute', inset: 0,
                background: 'rgba(62, 47, 47, 0.45)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
            }} />

            {/* Glass pane */}
            <div onClick={(e) => e.stopPropagation()} style={{
                position: 'relative',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(251,252,253,0.95) 40%, rgba(246,248,250,0.97) 100%)',
                borderRadius: 28,
                border: '1px solid rgba(255,255,255,0.6)',
                boxShadow: '0 32px 80px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.3) inset, 0 1px 3px rgba(255,255,255,0.5) inset',
                maxWidth: 640,
                width: '100%',
                maxHeight: '88vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            }}>
                {/* Specular highlight */}
                <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: '40%',
                    background: 'radial-gradient(ellipse 60% 70% at 30% 0%, rgba(255,255,255,0.35) 0%, transparent 60%)',
                    pointerEvents: 'none', zIndex: 0, borderRadius: '28px 28px 0 0',
                }} />

                {/* Close button */}
                <button onClick={onClose} style={{
                    position: 'absolute', top: 16, right: 20, background: 'rgba(255,255,255,0.7)',
                    border: 'none', fontSize: 20, color: 'var(--mom-warm-gray)', cursor: 'pointer',
                    width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 10, backdropFilter: 'blur(8px)', transition: 'all 0.2s',
                }} aria-label="Close">✕</button>

                {/* Scrollable content */}
                <div style={{ overflowY: 'auto', padding: '40px 36px 36px', position: 'relative', zIndex: 1 }}>
                    {/* Hero */}
                    <div style={{ textAlign: 'center', marginBottom: 28 }}>
                        {hasPhoto ? (
                            <div style={{
                                width: 120, height: 120, borderRadius: '50%', margin: '0 auto 20px',
                                overflow: 'hidden',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.1), 0 0 0 3px rgba(255,255,255,0.7), 0 0 0 5px color-mix(in srgb, var(--mom-primary) 15%, transparent)',
                            }}>
                                <img src={c.aboutPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} referrerPolicy="no-referrer" />
                            </div>
                        ) : (
                            <div style={{
                                width: 100, height: 100, borderRadius: '50%', margin: '0 auto 20px',
                                background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 8px 24px color-mix(in srgb, var(--mom-primary) 20%, transparent)',
                            }}>
                                <span style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 40, fontWeight: 700, color: 'var(--mom-white)', fontStyle: 'italic' }}>D</span>
                            </div>
                        )}
                        <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 'clamp(24px, 4vw, 32px)', fontStyle: 'italic', fontWeight: 700, color: 'var(--mom-charcoal)', lineHeight: 1.2, marginBottom: tagline ? 8 : 0 }}>
                            {headline}
                        </h2>
                        {tagline && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-primary)', fontWeight: 600 }}>{tagline}</p>}
                    </div>

                    {/* Intro */}
                    {intro && (
                        <div style={{ padding: '24px', background: 'rgba(255,255,255,0.5)', borderRadius: 18, marginBottom: 16, border: '1px solid rgba(255,255,255,0.4)' }}>
                            <FormattedText text={intro} style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 16, fontStyle: 'italic', color: 'var(--mom-charcoal)', lineHeight: 1.7, textAlign: 'center' }} />
                        </div>
                    )}

                    {/* Body */}
                    {body && (
                        <div style={{ padding: '24px', background: 'rgba(255,255,255,0.5)', borderRadius: 18, marginBottom: 16, border: '1px solid rgba(255,255,255,0.4)' }}>
                            <FormattedText text={body} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-charcoal)', lineHeight: 1.8 }} />
                        </div>
                    )}

                    {/* Closing */}
                    {(closingHeadline || closing) && (
                        <div style={{ padding: '24px', background: 'rgba(255,255,255,0.5)', borderRadius: 18, marginBottom: 16, border: '1px solid rgba(255,255,255,0.4)', textAlign: 'center' }}>
                            {closingHeadline && <h3 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 20, fontStyle: 'italic', fontWeight: 700, color: 'var(--mom-charcoal)', marginBottom: 10 }}>{closingHeadline}</h3>}
                            {closing && <FormattedText text={closing} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-warm-gray)', lineHeight: 1.8 }} />}
                        </div>
                    )}

                    {/* CTA */}
                    {ctaText && (
                        <div style={{ textAlign: 'center', marginTop: 24 }}>
                            <button className="mom-btn mom-btn-primary" onClick={() => {
                                onClose();
                                if (ctaLink.startsWith('/') && onNavigate) onNavigate(ctaLink);
                                else if (!ctaLink.startsWith('/')) window.open(ctaLink, '_blank');
                            }} style={{ padding: '14px 40px', fontSize: 15, borderRadius: 50 }}>
                                {ctaText} →
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
